<template>
  <div>
    <!-- En-tête de page avec accessibilité et guidance -->
    <div
      class="page-header mb-4"
      aria-label="En-tête de la gestion des livreurs"
    >
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1
            class="page-title mb-0"
            aria-label="Gestion professionnelle des livreurs"
          >
            <feather-icon
              icon="UsersIcon"
              size="24"
              class="mr-2"
              aria-hidden="true"
            />
            Gestion des livreurs
          </h1>
          <p
            id="page-desc"
            class="text-muted mb-0"
          >
            Consultez, filtrez et gérez les livreurs de votre flotte logistique. Utilisez les filtres avancés pour affiner la recherche et surveillez les indicateurs clés de performance.
          </p>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            v-b-tooltip.hover
            title="Actualiser les données de la flotte logistique"
            variant="outline-primary"
            class="mr-2"
            :disabled="isLoading"
            aria-label="Actualiser la liste des livreurs"
            @click="refreshData"
          >
            <feather-icon
              :icon="isLoading ? 'RefreshCwIcon' : 'RefreshCwIcon'"
              size="16"
              :class="{ 'rotate-animation': isLoading }"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            v-if="user && user.role === 'admin'"
            :to="{ name: 'delivery-man-create' }"
            variant="primary"
            class="btn-gradient"
            aria-label="Ajouter un nouveau livreur à la flotte logistique"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              aria-hidden="true"
            />
            <span class="ml-1">Nouveau livreur</span>
          </b-button>
        </div>
      </div>
      <div
        class="sr-only"
        aria-live="polite"
      >
        Utilisez les filtres pour rechercher un livreur par nom, dépôt ou statut. Les actions sont accessibles via les boutons à droite de chaque ligne du tableau.
      </div>
    </div>

    <!-- Cartes de statistiques améliorées pour la flotte -->
    <b-row
      class="mb-4"
      aria-label="Statistiques de la flotte de livreurs"
    >
      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card
          class="stats-card stats-neutral h-100"
          aria-label="Nombre total de livreurs"
        >
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h3
                class="font-weight-bolder mb-0 text-dark"
                aria-label="Total livreurs"
              >
                {{ deliveryManStats.total }}
              </h3>
              <p class="card-text mb-0">
                Total Livreurs
              </p>
              <small class="text-muted">
                Équipe complète
              </small>
            </div>
            <div class="avatar bg-light-secondary p-50">
              <feather-icon
                icon="UsersIcon"
                size="24"
                class="text-secondary"
              />
            </div>
          </div>
          <div class="stats-progress mt-3">
            <div class="progress progress-sm">
              <div
                class="progress-bar bg-secondary"
                style="width: 100%"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="stats-card stats-neutral h-100">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="font-weight-bolder mb-0 text-success">
                {{ deliveryManStats.active }}
              </h3>
              <p class="card-text mb-0">
                Actifs
              </p>
              <small class="text-muted">
                {{ getPercentage(deliveryManStats.active, deliveryManStats.total) }}% disponibles
              </small>
            </div>
            <div class="avatar bg-light-success p-50">
              <feather-icon
                icon="UserCheckIcon"
                size="24"
                class="text-success"
              />
            </div>
          </div>
          <div class="stats-progress mt-3">
            <div class="progress progress-sm">
              <div
                class="progress-bar bg-success"
                :style="`width: ${getPercentage(deliveryManStats.active, deliveryManStats.total)}%`"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="stats-card stats-neutral h-100">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="font-weight-bolder mb-0 text-danger">
                {{ deliveryManStats.inactive }}
              </h3>
              <p class="card-text mb-0">
                Inactifs
              </p>
              <small class="text-muted">
                {{ getPercentage(deliveryManStats.inactive, deliveryManStats.total) }}% indisponibles
              </small>
            </div>
            <div class="avatar bg-light-danger p-50">
              <feather-icon
                icon="UserXIcon"
                size="24"
                class="text-danger"
              />
            </div>
          </div>
          <div class="stats-progress mt-3">
            <div class="progress progress-sm">
              <div
                class="progress-bar bg-danger"
                :style="`width: ${getPercentage(deliveryManStats.inactive, deliveryManStats.total)}%`"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="stats-card stats-neutral h-100">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="font-weight-bolder mb-0 text-info">
                {{ deliveryManStats.withCars }}
              </h3>
              <p class="card-text mb-0">
                Avec Véhicule
              </p>
              <small class="text-muted">
                {{ getPercentage(deliveryManStats.withCars, deliveryManStats.total) }}% équipés
              </small>
            </div>
            <div class="avatar bg-light-info p-50">
              <feather-icon
                icon="TruckIcon"
                size="24"
                class="text-info"
              />
            </div>
          </div>
          <div class="stats-progress mt-3">
            <div class="progress progress-sm">
              <div
                class="progress-bar bg-info"
                :style="`width: ${getPercentage(deliveryManStats.withCars, deliveryManStats.total)}%`"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Enhanced Search and filter bar -->
    <b-card class="mb-4 filter-card">
      <div class="filter-header mb-3">
        <h5 class="mb-0">
          <feather-icon
            icon="FilterIcon"
            size="18"
            class="mr-2"
          />
          Filtres et Recherche
        </h5>
        <small class="text-muted">
          Trouvez rapidement vos livreurs
        </small>
      </div>

      <b-row>
        <!-- Search Field -->
        <b-col
          md="4"
          sm="6"
          class="mb-3"
        >
          <b-form-group label="Recherche Globale">
            <b-input-group class="search-input-group">
              <b-form-input
                v-model="searchTerm"
                placeholder="Nom, email, téléphone..."
                class="enhanced-input"
                @input="onSearchChange"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  class="search-btn"
                  @click="getDeliveryMans"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Repository Filter: Only show for admin -->
        <b-col
          v-if="user && user.role === 'admin'"
          md="4"
          sm="6"
          class="mb-3"
        >
          <b-form-group label="Filtrer par Dépôt">
            <v-select
              v-model="repositoryFilter"
              :options="repositories"
              label="name"
              placeholder="Tous les dépôts"
              :reduce="repo => repo.id"
              clearable
              class="enhanced-select"
              @input="getDeliveryMans"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="HomeIcon"
                    size="14"
                    class="mr-2 text-muted"
                  />
                  {{ option.name }}
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Status Filter -->
        <b-col
          md="4"
          sm="6"
          class="mb-3"
        >
          <b-form-group label="Filtrer par Statut">
            <v-select
              v-model="statusFilter"
              :options="statusOptions"
              label="text"
              placeholder="Tous les statuts"
              :reduce="status => status.value"
              clearable
              class="enhanced-select"
              @input="getDeliveryMans"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <b-badge
                    :variant="option.variant"
                    pill
                    class="mr-2"
                  >
                    {{ option.text }}
                  </b-badge>
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Action Buttons Row -->
      <b-row class="mt-3">
        <b-col class="d-flex justify-content-between align-items-center flex-wrap">
          <!-- Left Actions -->
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <b-button-group class="mr-3">
              <b-button
                v-b-tooltip.hover
                title="Réinitialiser tous les filtres"
                variant="outline-secondary"
                @click="resetFilters"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="16"
                />
                <span class="d-none d-lg-inline ml-1">Reset</span>
              </b-button>

              <b-button
                v-b-tooltip.hover
                :title="`Basculer vers la vue ${viewMode === 'table' ? 'carte' : 'tableau'}`"
                :variant="viewMode === 'table' ? 'outline-secondary' : 'outline-primary'"
                @click="toggleViewMode"
              >
                <feather-icon
                  :icon="viewMode === 'table' ? 'GridIcon' : 'ListIcon'"
                  size="16"
                />
                <span class="d-none d-lg-inline ml-1">
                  {{ viewMode === 'table' ? 'Carte' : 'Liste' }}
                </span>
              </b-button>
            </b-button-group>

            <!-- Results Counter -->
            <small class="text-muted">
              {{ sortedDeliveryMans.length }} résultat(s) trouvé(s)
              <span v-if="hasActiveFilters">(filtré)</span>
            </small>
          </div>

          <!-- Right Actions -->
          <div class="d-flex align-items-center">
            <b-button
              v-b-tooltip.hover
              title="Exporter la liste actuelle en CSV"
              variant="outline-success"
              class="mr-2"
              :disabled="!sortedDeliveryMans.length"
              @click="exportToCSV"
            >
              <feather-icon
                icon="DownloadIcon"
                size="16"
              />
              <span class="d-none d-md-inline ml-1">Export CSV</span>
            </b-button>

            <b-button
              v-if="user && user.role === 'admin'"
              :to="{ name: 'delivery-man-create' }"
              variant="primary"
              class="btn-gradient"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
              />
              <span class="ml-1">Ajouter</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Active Filters Display -->
      <div
        v-if="hasActiveFilters"
        class="active-filters mt-3 pt-3 border-top"
      >
        <div class="d-flex align-items-center flex-wrap">
          <small class="text-muted mr-2 mb-1">
            <feather-icon
              icon="FilterIcon"
              size="12"
              class="mr-1"
            />
            Filtres actifs:
          </small>

          <b-badge
            v-if="searchTerm"
            variant="light-primary"
            class="filter-badge mr-2 mb-1"
          >
            <feather-icon
              icon="SearchIcon"
              size="12"
              class="mr-1"
            />
            "{{ searchTerm }}"
            <feather-icon
              icon="XIcon"
              size="12"
              class="ml-1 cursor-pointer"
              @click="searchTerm = ''; getDeliveryMans()"
            />
          </b-badge>

          <b-badge
            v-if="repositoryFilter"
            variant="light-info"
            class="filter-badge mr-2 mb-1"
          >
            <feather-icon
              icon="HomeIcon"
              size="12"
              class="mr-1"
            />
            {{ getRepositoryName(repositoryFilter) }}
            <feather-icon
              icon="XIcon"
              size="12"
              class="ml-1 cursor-pointer"
              @click="repositoryFilter = null; getDeliveryMans()"
            />
          </b-badge>

          <b-badge
            v-if="statusFilter !== null"
            variant="light-warning"
            class="filter-badge mr-2 mb-1"
          >
            <feather-icon
              icon="UserIcon"
              size="12"
              class="mr-1"
            />
            {{ getStatusText(statusFilter) }}
            <feather-icon
              icon="XIcon"
              size="12"
              class="ml-1 cursor-pointer"
              @click="statusFilter = null; getDeliveryMans()"
            />
          </b-badge>

          <b-button
            variant="outline-secondary"
            size="sm"
            class="clear-filters-btn ml-2 mb-1"
            @click="resetFilters"
          >
            <feather-icon
              icon="XIcon"
              size="12"
            />
            <span class="ml-1">Tout effacer</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Enhanced Loading state -->
    <div
      v-if="isLoading"
      class="loading-state text-center py-5"
    >
      <div class="loading-spinner mb-3">
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem;"
        />
      </div>
      <h5 class="text-muted mb-2">
        Chargement en cours...
      </h5>
      <p class="text-muted mb-0">
        Récupération des données des livreurs
      </p>
    </div>

    <!-- Enhanced Empty state -->
    <b-card
      v-else-if="!sortedDeliveryMans.length"
      class="empty-state-card text-center py-5"
    >
      <div class="empty-state">
        <div class="empty-state-icon mb-4">
          <div class="icon-wrapper">
            <feather-icon
              :icon="hasActiveFilters ? 'SearchIcon' : 'UsersIcon'"
              size="64"
              class="text-muted"
            />
          </div>
        </div>

        <h4 class="empty-title mb-3">
          {{ hasActiveFilters ? 'Aucun résultat trouvé' : 'Aucun livreur enregistré' }}
        </h4>

        <p class="empty-description text-muted mb-4">
          <span v-if="hasActiveFilters">
            Aucun livreur ne correspond à vos critères de recherche.
            <br>
            Essayez de modifier vos filtres ou d'effacer la recherche pour voir plus de résultats.
          </span>
          <span v-else>
            Il n'y a pas encore de livreurs enregistrés dans le système.
            <br>
            Commencez par ajouter votre premier livreur pour démarrer la gestion de votre équipe.
          </span>
        </p>

        <div class="empty-actions">
          <b-button
            v-if="hasActiveFilters"
            variant="outline-primary"
            class="mr-2"
            @click="resetFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
            />
            <span class="ml-1">Effacer les filtres</span>
          </b-button>

          <b-button
            v-if="user && user.role === 'admin'"
            :to="{ name: 'delivery-man-create' }"
            variant="primary"
            class="btn-gradient"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            <span class="ml-1">{{ hasActiveFilters ? 'Nouveau livreur' : 'Ajouter le premier livreur' }}</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Enhanced Card view -->
    <div
      v-else-if="viewMode === 'card'"
      class="card-view-container"
    >
      <b-row class="match-height">
        <b-col
          v-for="deliveryMan in sortedDeliveryMans"
          :key="deliveryMan.id"
          lg="4"
          md="6"
          sm="12"
          class="mb-4"
        >
          <b-card class="delivery-man-card enhanced-card h-100">
            <!-- Card Header -->
            <div class="card-header-section d-flex justify-content-between align-items-start">
              <div class="d-flex align-items-center flex-grow-1">
                <div class="user-avatar-wrapper">
                  <b-avatar
                    :text="getInitials(deliveryMan.username)"
                    :variant="deliveryMan.is_active ? 'light-success' : 'light-secondary'"
                    size="xl"
                    class="user-avatar"
                  />
                  <div
                    v-if="deliveryMan.is_active"
                    class="status-indicator active"
                  />
                  <div
                    v-else
                    class="status-indicator inactive"
                  />
                </div>
                <div class="user-info ml-3 flex-grow-1">
                  <h5 class="user-name mb-1">
                    {{ deliveryMan.username }}
                  </h5>
                  <small class="user-email text-muted d-block">
                    {{ deliveryMan.email }}
                  </small>
                  <small class="user-role text-muted">
                    {{ getRoleInFrench(deliveryMan.role) }}
                  </small>
                </div>
              </div>
              <div class="card-status">
                <b-badge
                  :variant="deliveryMan.is_active ? 'success' : 'danger'"
                  pill
                  class="status-badge"
                >
                  {{ deliveryMan.is_active ? 'Actif' : 'Inactif' }}
                </b-badge>
              </div>
            </div>

            <!-- Card Content -->
            <div class="card-content mt-3">
              <div class="info-grid">
                <div class="info-item phone-info">
                  <div class="info-icon">
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                      class="text-primary"
                    />
                  </div>
                  <div class="info-content">
                    <div class="info-label">
                      Téléphone
                    </div>            <div class="info-value">
                      <a
                        v-if="deliveryMan.phone"
                        :href="`tel:${deliveryMan.phone}`"
                        class="phone-link"
                      >
                        {{ deliveryMan.phone }}
                      </a>
                      <span
                        v-else
                        class="text-muted"
                      >
                        Non défini
                      </span>
                    </div>
                  </div>
                </div>

                <div class="info-item repository-info">
                  <div class="info-icon">
                    <feather-icon
                      icon="HomeIcon"
                      size="16"
                      class="text-info"
                    />
                  </div>
                  <div class="info-content">
                    <div class="info-label">
                      Dépôt
                    </div>
                    <div class="info-value">
                      <b-badge
                        v-if="deliveryMan.repository_name"
                        variant="light-info"
                        class="repository-badge"
                      >
                        {{ deliveryMan.repository_name }}
                      </b-badge>
                      <span
                        v-else
                        class="text-muted"
                      >
                        Non assigné
                      </span>
                    </div>
                  </div>
                </div>

                <div class="info-item vehicle-info">
                  <div class="info-icon">
                    <feather-icon
                      icon="TruckIcon"
                      size="16"
                      class="text-success"
                    />
                  </div>
                  <div class="info-content">
                    <div class="info-label">
                      Véhicule
                    </div>
                    <div class="info-value">
                      <div
                        v-if="deliveryMan.car_details"
                        class="vehicle-details"
                      >
                        <strong>{{ deliveryMan.car_details.brand }}</strong>
                        <small
                          v-if="deliveryMan.car_details.model"
                          class="d-block text-muted"
                        >
                          {{ deliveryMan.car_details.model }}
                        </small>
                      </div>
                      <span
                        v-else
                        class="text-muted"
                      >
                        Aucun véhicule
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Additional info: Join date -->
                <div class="info-item join-date-info">
                  <div class="info-icon">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="text-secondary"
                    />
                  </div>
                  <div class="info-content">
                    <div class="info-label">
                      Membre depuis
                    </div>
                    <div class="info-value">
                      {{ formatJoinDate(deliveryMan.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card Footer -->
            <div class="card-footer-section mt-3 pt-3 border-top">
              <div class="d-flex justify-content-between align-items-center">
                <div class="card-stats">
                  <b-badge
                    v-if="getOrdersCount(deliveryMan)"
                    variant="light-warning"
                    class="orders-badge"
                  >
                    <feather-icon
                      icon="PackageIcon"
                      size="12"
                      class="mr-1"
                    />
                    {{ deliveryMan.active_orders_count || 0 }} commande(s)
                  </b-badge>
                  <small
                    v-else
                    class="text-muted"
                  >
                    Aucune commande active
                  </small>
                </div>
                <div class="card-actions">
                  <b-button-group size="sm">
                    <b-button
                      v-b-tooltip.hover
                      title="Voir les détails"
                      variant="outline-info"
                      class="btn-icon action-btn"
                      :to="{ name: 'delivery-man-show', params: { id: deliveryMan.id }}"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="16"
                      />
                    </b-button>
                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      title="Modifier"
                      variant="outline-primary"
                      class="btn-icon action-btn"
                      :to="{ name: 'delivery-man-edit', params: { id: deliveryMan.id }}"
                    >
                      <feather-icon
                        icon="EditIcon"
                        size="16"
                      />
                    </b-button>
                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      :title="deliveryMan.is_active ? 'Désactiver' : 'Activer'"
                      :variant="deliveryMan.is_active ? 'outline-warning' : 'outline-success'"
                      class="btn-icon action-btn"
                      @click="toggleDeliveryManStatus(deliveryMan)"
                    >
                      <feather-icon
                        :icon="deliveryMan.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                        size="16"
                      />
                    </b-button>
                  </b-button-group>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Table View with High Visibility Design -->
    <vue-good-table
      v-else
      :key="tableKey"
      :columns="columns"
      :rows="sortedDeliveryMans"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm,
      }"
      :pagination-options="{
        enabled: true,
        perPage: pageLength,
      }"
      style-class="vgt-table bordered striped hover"
      class="delivery-man-table"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Column: Name -->
        <div
          v-if="props.column.field === 'username'"
          class="text-nowrap"
        >
          <b-avatar
            :text="getInitials(props.row.username)"
            class="mx-1"
          />
          <span class="text-nowrap">{{ props.row.username }}</span>
        </div>

        <!-- Column: Status -->
        <div v-else-if="props.column.field === 'is_active'">
          <b-badge
            :variant="props.row.is_active ? 'success' : 'danger'"
            pill
          >
            {{ props.row.is_active ? 'Actif' : 'Inactif' }}
          </b-badge>
        </div>

        <!-- Column: Car -->
        <div v-else-if="props.column.field === 'car_details'">
          <span>{{ props.row.car_details ? props.row.car_details.brand : 'Aucun véhicule' }}</span>
        </div>

        <!-- Column: Action -->
        <div
          v-else-if="props.column.field === 'action'"
          class="d-flex"
        >
          <b-button
            v-b-tooltip.nofade.top
            title="Voir détails"
            class="btn-icon rounded-circle mr-1"
            size="sm"
            variant="flat-info"
            :to="{ name: 'delivery-man-show', params: { id: props.row.id }}"
          >
            <feather-icon
              icon="EyeIcon"
              size="16"
            />
          </b-button>

          <b-button
            v-if="user && user.role === 'admin'"
            v-b-tooltip.nofade.top
            title="Modifier"
            class="btn-icon rounded-circle mr-1"
            size="sm"
            variant="flat-primary"
            :to="{ name: 'delivery-man-edit', params: { id: props.row.id }}"
          >
            <feather-icon
              icon="EditIcon"
              size="16"
            />
          </b-button>

          <b-button
            v-if="user && user.role === 'admin'"
            v-b-tooltip.nofade.top
            :title="props.row.is_active ? 'Désactiver' : 'Activer'"
            class="btn-icon rounded-circle"
            size="sm"
            :variant="props.row.is_active ? 'flat-warning' : 'flat-success'"
            @click="toggleDeliveryManStatus(props.row)"
          >
            <feather-icon
              :icon="props.row.is_active ? 'UserXIcon' : 'UserCheckIcon'"
              size="16"
            />
          </b-button>
        </div>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap"> Affichage 1 à </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="
                (value) => props.perPageChanged({ currentPerPage: value })
              "
            />
            <span class="text-nowrap"> de {{ props.total }} résultats trouvés </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value) => props.pageChanged({ currentPage: value })"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Status Toggle Confirmation Modal -->
    <b-modal
      ref="statusModal"
      :title="selectedDeliveryMan && selectedDeliveryMan.is_active ? 'Désactiver le livreur' : 'Activer le livreur'"
      ok-title="Confirmer"
      cancel-title="Annuler"
      ok-variant="primary"
      @ok="handleStatusToggle"
    >
      <p v-if="selectedDeliveryMan">
        Êtes-vous sûr de vouloir {{ selectedDeliveryMan.is_active ? 'désactiver' : 'activer' }}
        le livreur <strong>{{ selectedDeliveryMan.username }}</strong> ?
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar,
  BPagination,
  BFormSelect,
  BButton,
  BSpinner,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCard,
  BBadge,
  BRow,
  BCol,
  BButtonGroup,
  BModal,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  name: 'ListDrivers',
  components: {
    VueGoodTable,
    BAvatar,
    BPagination,
    BFormSelect,
    BButton,
    BSpinner,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCard,
    BBadge,
    BRow,
    BCol,
    BButtonGroup,
    BModal,
    vSelect,
  },

  data() {
    return {
      isLoading: false,
      pageLength: 100,
      dir: false,
      columns: [
        {
          label: 'Livreur',
          field: 'username',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher livreur',
          },
        },
        {
          label: 'Email',
          field: 'email',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher email',
          },
        },
        {
          label: 'Téléphone',
          field: 'phone',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher téléphone',
          },
        },
        {
          label: 'Téléphone secondaire',
          field: 'phone2',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher téléphone secondaire',
          },
        },
        {
          label: 'Dépôt',
          field: 'repository_name',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher dépôt',
          },
        },
        {
          label: 'Véhicule',
          field: 'car_details',
          sortable: true,
        },
        {
          label: 'Statut',
          field: 'is_active',
          sortable: true,
        },
        {
          label: 'Action',
          field: 'action',
          sortable: false,
        },
      ],
      delivery_mans: [],
      searchTerm: '',
      user: {},
      repositoryFilter: null,
      statusFilter: null,
      repositories: [],
      selectedDeliveryMan: null,
      viewMode: 'table', // 'card' or 'table'
      deliveryManStats: {
        total: 0,
        active: 0,
        inactive: 0,
        withCars: 0,
      },
      tableKey: 0,
      searchTimer: null,
      statusOptions: [
        { value: true, text: 'Actif', variant: 'success' },
        { value: false, text: 'Inactif', variant: 'danger' },
      ],
    }
  },

  computed: {
    hasActiveFilters() {
      return this.searchTerm || this.repositoryFilter || this.statusFilter !== null
    },

    sortedDeliveryMans() {
      let filtered = [...this.delivery_mans]

      // Apply status filter if set
      if (this.statusFilter !== null) {
        filtered = filtered.filter(dm => dm.is_active === this.statusFilter)
      }

      // If no filters are applied, sort by active status first (active first, then inactive)
      if (!this.hasActiveFilters) {
        return filtered.sort((a, b) => {
          // Active users first (true comes before false)
          if (a.is_active !== b.is_active) {
            return b.is_active - a.is_active
          }
          // If same status, sort alphabetically by username
          return (a.username || '').localeCompare(b.username || '')
        })
      }
      // If filters are applied, return filtered results
      return filtered
    },
  },

  created() {
    this.getDeliveryMans()
    this.user = storeAuth.state.user
    if (this.user && this.user.role === 'admin') {
      this.fetchRepositories()
    }
  },

  methods: {
    async getDeliveryMans() {
      this.isLoading = true
      try {
        // Use the new dedicated endpoint instead of filtering
        const params = {}
        if (this.repositoryFilter) {
          params.repository = this.repositoryFilter
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }
        if (this.statusFilter !== null) {
          params.is_active = this.statusFilter
        }

        const { data } = await axios.get('/api/auth/users/delivery-men/', { params })
        this.delivery_mans = data
        this.calculateStats()
        this.tableKey += 1 // Force table re-render
      } catch (error) {
        this.showToast('danger', 'top-center', 'Error loading delivery men')
      } finally {
        this.isLoading = false
      }
    },

    refreshData() {
      this.getDeliveryMans()
    },

    getPercentage(value, total) {
      if (total === 0) return 0
      return Math.round((value / total) * 100)
    },

    getRoleInFrench(role) {
      const roleMappings = {
        delivery_man: 'Livreur',
        admin: 'Administrateur',
        manager: 'Manager',
        user: 'Utilisateur',
      }
      return roleMappings[role] || role
    },

    getStatusText(statusValue) {
      return statusValue ? 'Actif' : 'Inactif'
    },
    deleteDeliveryMen(id) {
      // This method is deprecated - use toggleDeliveryManStatus instead
      this.toggleDeliveryManStatus({ id })
    },

    async toggleDeliveryManStatus(deliveryMan) {
      try {
        const result = await this.$swal({
          title: deliveryMan.is_active ? 'Désactiver le livreur' : 'Activer le livreur',
          text: `Êtes-vous sûr de vouloir ${deliveryMan.is_active ? 'désactiver' : 'activer'} ce livreur?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: deliveryMan.is_active ? 'Oui, désactiver' : 'Oui, activer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          this.isLoading = true
          try {
            const response = await axios.post(`/api/auth/users/delivery-men/toggle-activation/${deliveryMan.id}/`)
            this.showToast(
              'success',
              'top-center',
              `Livreur ${response.data.is_active ? 'activé' : 'désactivé'} avec succès`,
            )
            await this.getDeliveryMans()
          } catch (error) {
            this.showToast('danger', 'top-center', error.response?.data?.error || 'Erreur lors du changement de statut')
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed, do nothing
      }
    },

    calculateStats() {
      this.deliveryManStats = {
        total: this.delivery_mans.length,
        active: this.delivery_mans.filter(dm => dm.is_active).length,
        inactive: this.delivery_mans.filter(dm => !dm.is_active).length,
        withCars: this.delivery_mans.filter(dm => dm.car_details).length,
      }
    },

    // toast
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    getOrdersCount(deliveryMan) {
      // Use actual order count if available from API
      return deliveryMan.active_orders_count || 0
    },

    async fetchRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des dépôts')
      }
    },

    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'card' : 'table'
    },

    onSearchChange() {
      // Debounced search
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.getDeliveryMans()
      }, 500)
    },

    resetFilters() {
      this.searchTerm = ''
      this.repositoryFilter = null
      this.statusFilter = null
      this.getDeliveryMans()
    },

    confirmToggleStatus(deliveryMan) {
      this.selectedDeliveryMan = deliveryMan
      this.$refs.statusModal.show()
    },

    async handleStatusToggle() {
      if (!this.selectedDeliveryMan) return

      try {
        this.isLoading = true
        const response = await axios.post(`/api/auth/users/delivery-men/toggle-activation/${this.selectedDeliveryMan.id}/`)

        this.showToast(
          'success',
          'top-center',
          `Livreur ${response.data.is_active ? 'activé' : 'désactivé'} avec succès`,
        )

        // Refresh the entire list to ensure data consistency and table reactivity
        await this.getDeliveryMans()
      } catch (error) {
        this.showToast('danger', 'top-center', error.response?.data?.error || 'Erreur lors du changement de statut')
      } finally {
        this.isLoading = false
      }
    },

    exportToCSV() {
      try {
        // Format data for CSV
        const csvData = this.delivery_mans.map(driver => ({
          Nom: driver.username || '',
          Email: driver.email || '',
          Téléphone: driver.phone || '',
          Dépôt: driver.repository_name || '',
          Véhicule: driver.car_details ? `${driver.car_details.brand} ${driver.car_details.model}` : '',
          Statut: driver.is_active ? 'Actif' : 'Inactif',
        }))

        // Convert to CSV string
        const headers = Object.keys(csvData[0]).join(',')
        const rows = csvData.map(obj => Object.values(obj).map(val => `"${val}"`).join(','))
        const csvContent = [headers, ...rows].join('\n')

        // Create blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', `livreurs_${new Date().toISOString().slice(0, 10)}.csv`)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.showToast('success', 'top-right', 'Données exportées avec succès')
      } catch (error) {
        console.error('Error exporting to CSV:', error)
        this.showToast('danger', 'top-right', "Erreur lors de l'exportation")
      }
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    getRepositoryName(repositoryId) {
      const repo = this.repositories.find(r => r.id === repositoryId)
      return repo ? repo.name : 'Inconnu'
    },

    formatJoinDate(dateString) {
      if (!dateString) return 'Non défini'
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays < 30) {
        return `${diffDays} jour(s)`
      } if (diffDays < 365) {
        const months = Math.floor(diffDays / 30)
        return `${months} mois`
      }
      const years = Math.floor(diffDays / 365)
      return `${years} an(s)`
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/user-modules.scss";

// Custom styles specific to this view
.delivery-man-card {
  @extend .user-card;
}

.delivery-man-table {
  ::v-deep .vgt-table {
    @extend .enhanced-table;
  }
}

// Apply v-select-enhanced to all v-selects in this component
::v-deep .v-select {
  @extend .v-select-enhanced;
}

/* Enhanced repository selector styling */
.repository-selector {
  /* Apply enhanced styling from user-modules.scss */
  @extend .v-select-enhanced;

  /* Additional styling specific to repository selector */
  .vs__dropdown-toggle {
    border: 1px solid #d8d6de;
    transition: all 0.3s;

    /* Add subtle box-shadow for better visibility */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  }

  .vs__dropdown-option {
    padding: 10px 20px;
    border-bottom: 1px solid #f3f2f7;

    &:last-child {
      border-bottom: none;
    }
  }

  &.vs--open {
    .vs__dropdown-toggle {
      box-shadow: 0 3px 10px rgba(115, 103, 240, 0.1);
    }
  }

  .vs__selected {
    padding: 2px 8px;
    margin: 0;
  }
}

/* Enhanced filtering dropdowns */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 4px 8px;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem;
    background-color: #f8f8f8;
    transition: all 0.3s ease-in-out;
    min-height: 38px;

    &:hover {
      border-color: #7367f0 !important;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #7367f0 !important;
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.1);
  }

  .vs__selected {
    margin: 0 2px;
    padding: 0 5px;
    color: #5e5873;
  }

  .vs__search::placeholder {
    color: #b9b9c3;
  }

  .vs__dropdown-menu {
    padding: 0;
    border: 1px solid #d8d6de;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
}

.border-select .vs__dropdown-toggle {
  border: 1px solid #d8d6de !important;
}

/* Empty state styling */
.empty-state {
  padding: 2rem;

  .empty-state-icon {
    opacity: 0.6;
  }
}

/* Cursor pointer for interactive elements */
.cursor-pointer {
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

/* Add styles for user details */
.user-detail {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Statistics cards styling */
.stats-card {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.1);
  }

  .avatar {
    height: 3.5rem;
    width: 3.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.357rem;
  }
}

/* Fix for table actions layout */
.vgt-table td .d-flex {
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
}

.vgt-table td .btn-icon {
  margin-right: 0.25rem;
}

/* Enhanced Card View Styling */
.card-view-container {
  .enhanced-card {
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #E80000, #ff4d4d);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 40px rgba(232, 0, 0, 0.15);
      border-color: #E80000;

      &::before {
        opacity: 1;
      }

      .user-avatar {
        transform: scale(1.05);
      }

      .action-btn {
        transform: translateY(-2px);
      }
    }

    .card-header-section {
      padding: 1.5rem 1.5rem 1rem;

      .user-avatar-wrapper {
        position: relative;

        .user-avatar {
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .status-indicator {
          position: absolute;
          bottom: 2px;
          right: 2px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;

          &.active {
            background-color: #28a745;
            box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.2);
          }

          &.inactive {
            background-color: #dc3545;
            box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2);
          }
        }
      }

      .user-info {
        .user-name {
          font-weight: 600;
          color: #2c2c54;
          margin-bottom: 0.25rem;
        }

        .user-email {
          font-size: 0.875rem;
          color: #6c757d;
          margin-bottom: 0.25rem;
        }

        .user-role {
          font-size: 0.75rem;
          color: #E80000;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .status-badge {
        font-weight: 600;
        padding: 0.5rem 1rem;
        font-size: 0.75rem;
        letter-spacing: 0.5px;
      }
    }

    .card-content {
      padding: 0 1.5rem;

      .info-grid {
        display: grid;
        gap: 1rem;
        grid-template-columns: repeat(2, 1fr);

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }

        .info-item {
          display: flex;
          align-items: center;
          padding: 0.75rem;
          background: #f8f9fa;
          border-radius: 0.375rem;
          transition: all 0.3s ease;

          &:hover {
            background: #e9ecef;
            transform: translateX(5px);
          }

          &.phone-info:hover {
            background: linear-gradient(45deg, #e3f2fd, #f3e5f5);
          }

          &.repository-info:hover {
            background: linear-gradient(45deg, #e8f5e8, #f0f8ff);
          }

          &.vehicle-info:hover {
            background: linear-gradient(45deg, #f0fff0, #f5f5dc);
          }

          &.join-date-info:hover {
            background: linear-gradient(45deg, #fafafa, #f0f0f0);
          }

          .info-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            background: white;
            border-radius: 0.375rem;
            margin-right: 0.75rem;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .info-content {
            flex: 1;

            .info-label {
              font-size: 0.75rem;
              font-weight: 600;
              color: #6c757d;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 0.25rem;
            }

            .info-value {
              font-size: 0.875rem;
              font-weight: 500;
              color: #495057;

              .phone-link {
                color: #E80000;
                text-decoration: none;
                transition: all 0.3s ease;

                &:hover {
                  color: #cc0000;
                  text-decoration: underline;
                }
              }

              .repository-badge {
                font-weight: 600;
                padding: 0.25rem 0.5rem;
                border-radius: 0.25rem;
                background: linear-gradient(45deg, rgba(232, 0, 0, 0.1), rgba(232, 0, 0, 0.2));
                color: #E80000;
                border: 1px solid rgba(232, 0, 0, 0.2);
              }

              .vehicle-details {
                strong {
                  color: #28a745;
                }

                small {
                  font-size: 0.75rem;
                  margin-top: 0.125rem;
                }
              }
            }
          }
        }
      }
    }

    .card-footer-section {
      padding: 1rem 1.5rem 1.5rem;
      background: rgba(115, 103, 240, 0.02);

      .orders-badge {
        background: linear-gradient(45deg, #ffc107, #ffeb3b);
        color: #495057;
        font-weight: 600;
        border: none;
        padding: 0.5rem 0.75rem;
      }

      .action-btn {
        transition: all 0.3s ease;
        border-radius: 0.375rem !important;
        margin-left: 0.25rem;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      }
    }
  }
}

/* Enhanced Page Header */
.page-header {
  .page-title {
    font-weight: 700;
    color: #2c2c54;
  }

  .btn-gradient {
    background: linear-gradient(45deg, #E80000, #ff4d4d);
    border: none;
    box-shadow: 0 4px 15px rgba(232, 0, 0, 0.3);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(232, 0, 0, 0.4);
      background: linear-gradient(45deg, #cc0000, #E80000);
    }
  }

  .rotate-animation {
    animation: rotate 1s linear infinite;
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}

/* Enhanced Filter Card */
.filter-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

  .filter-header h5 {
    color: #2c2c54;
    font-weight: 600;
  }

  .enhanced-input {
    border: 1px solid #e9ecef;
    transition: all 0.3s ease;

    &:focus {
      border-color: #E80000;
      box-shadow: 0 3px 10px rgba(232, 0, 0, 0.1);
    }
  }

  .search-btn {
    background: linear-gradient(45deg, #E80000, #ff4d4d);
    border: none;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      background: linear-gradient(45deg, #cc0000, #E80000);
    }
  }

  .active-filters {
    .filter-badge {
      background: linear-gradient(45deg, #ffe6e6, #ffcccc);
      color: #E80000;
      border: 1px solid #ffcccc;
      padding: 0.5rem 0.75rem;
      border-radius: 1rem;
      font-weight: 500;
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(232, 0, 0, 0.2);
        background: linear-gradient(45deg, #ffdddd, #ffb3b3);
      }

      .cursor-pointer {
        margin-left: 0.5rem;
        padding: 0.25rem;
        border-radius: 50%;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(232, 0, 0, 0.1);
        }
      }
    }

    .clear-filters-btn {
      border-radius: 1rem;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }
  }
}

/* Enhanced Statistics Cards */
.stats-card {
  border-radius: 0.5rem;
  border: none;
  overflow: hidden;
  position: relative;

  &.stats-primary {
    background: linear-gradient(135deg, #E80000 0%, #ff4d4d 100%);
    color: white;

    .text-primary {
      color: white !important;
    }
  }

  &.stats-success {
    background: linear-gradient(135deg, #28c76f 0%, #20c997 100%);
    color: white;

    .text-success {
      color: white !important;
    }
  }

  &.stats-warning {
    background: linear-gradient(135deg, #ff9f43 0%, #fd7e14 100%);
    color: white;

    .text-warning {
      color: white !important;
    }
  }

  &.stats-info {
    background: linear-gradient(135deg, #00cfe8 0%, #1D1D1B 100%);
    color: white;

    .text-info {
      color: white !important;
    }
  }

  .stats-progress {
    .progress {
      height: 4px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;

      .progress-bar {
        background: rgba(255, 255, 255, 0.8);
        border-radius: 2px;
      }
    }
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
}

/* Enhanced Empty State */
.empty-state-card {
  border: 2px dashed #e9ecef;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);

  .empty-state {
    .icon-wrapper {
      display: inline-block;
      padding: 2rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-radius: 50%;
      margin-bottom: 1rem;
    }

    .empty-title {
      color: #2c2c54;
      font-weight: 600;
    }

    .empty-description {
      font-size: 1rem;
      line-height: 1.6;
    }

    .empty-actions .btn-gradient {
      background: linear-gradient(45deg, #E80000, #ff4d4d);
      border: none;
      box-shadow: 0 4px 15px rgba(232, 0, 0, 0.3);
    }
  }
}

/* Loading State Enhancement */
.loading-state {
  .loading-spinner {
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
}

/* Responsive Enhancements */
@media (max-width: 1200px) {
  .card-view-container .enhanced-card {
    .card-header-section {
      padding: 1.25rem 1.25rem 0.75rem;
    }

    .card-content {
      padding: 0 1.25rem;
    }

    .card-footer-section {
      padding: 0.75rem 1.25rem 1.25rem;
    }
  }
}

@media (max-width: 768px) {
  .page-header {
    .d-flex {
      flex-direction: column;
      align-items: flex-start !important;

      .d-flex {
        margin-top: 1rem;
        width: 100%;
        justify-content: flex-end;
      }
    }
  }

  .card-view-container .enhanced-card {
    .card-header-section {
      .d-flex {
        flex-direction: column;
        align-items: flex-start !important;

        .user-avatar-wrapper {
          margin-bottom: 1rem;
        }

        .card-status {
          align-self: flex-end;
          margin-top: -3rem;
        }
      }
    }

    .card-footer-section {
      .d-flex {
        flex-direction: column;
        align-items: flex-start !important;

        .card-actions {
          margin-top: 0.75rem;
          width: 100%;

          .b-button-group {
            width: 100%;

            .action-btn {
              flex: 1;
            }
          }
        }
      }
    }
  }

  .filter-card {
    .d-flex {
      flex-direction: column;
      align-items: flex-start !important;

      .d-flex {
        margin-top: 0.75rem;
        width: 100%;
      }
    }
  }
}

@media (max-width: 576px) {
  .stats-card {
    margin-bottom: 1rem !important;

    .d-flex {
      .avatar {
        display: none;
      }
    }
  }

  .card-view-container {
    .enhanced-card {
      margin-bottom: 1.5rem;

      .info-grid {
        gap: 0.75rem;
      }

      .card-actions .b-button-group {
        .action-btn {
          padding: 0.5rem;

          span {
            display: none;
          }
        }
      }
    }
  }
}

/* Animation Enhancements */
.card-view-container .enhanced-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-card {
  animation: fadeInDown 0.6s ease-out;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-card {
  animation: fadeInScale 0.6s ease-out;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Dark mode support (if needed) */
@media (prefers-color-scheme: dark) {
  .enhanced-card {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%) !important;
    color: #ecf0f1 !important;
    border-color: #34495e !important;

    .info-item {
      background: #34495e !important;
      color: #ecf0f1 !important;

      &:hover {
        background: #3c5470 !important;
      }
    }

    .user-name,
    .info-value {
      color: #ecf0f1 !important;
    }

    .info-label {
      color: #bdc3c7 !important;
    }
  }

  .filter-card {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%) !important;
    color: #ecf0f1 !important;
    border-color: #34495e !important;
  }
}
</style>
